import { getFieldComponentForType } from '@maison/components/form/fields';
import { localize, setLocale } from '@vee-validate/i18n';

import { confirmed, email, max, max_value, min, min_value, regex, required } from '@vee-validate/rules';
import { set } from 'lodash-es';
import { configure, defineRule } from 'vee-validate';

export const convertFormDataToData = (formData) => {
  const data = {};
  Object.keys(formData).forEach((fieldKey) => {
    const fieldName = formData[fieldKey].name || formData[fieldKey].id;
    data[fieldName] =
      formData[fieldKey] && formData[fieldKey].value !== undefined && formData[fieldKey].value !== null
        ? formData[fieldKey].value
        : '';
  });

  return data;
};

export const convertErrorsInFormData = (formData, errors) => {
  let captchaError = '';
  Object.keys(errors).forEach((field) => {
    // When Laravel validates an array, the error's index key will be a string made of the name of
    // the field + the index of the errored value, for instance : 'attachments.0: ['error']'.
    // Splitting at the first '.' allows us to extract the name of the field in the form.
    const fieldName = field.split('.').shift();
    const fieldInForm = findFieldInFormByName(formData, fieldName);
    const error = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
    if (fieldInForm) {
      set(fieldInForm, ['errors'], error);
    } else if (field === 'captchaToken') {
      captchaError = error;
    }
  });

  return captchaError;
};

export const findFieldInFormByName = (form, fieldName) => {
  for (const key in form) {
    if (form[key].name === fieldName) {
      return form[key];
    }
  }

  return null;
};

export const getTextDictionaryForForm = () => {
  const translations = window.Phoenix.i18n;
  const fallbackLocale = window.Phoenix.laravelLanguage;
  const dictionary = {};

  dictionary[translations.locale] = {
    messages: {
      _default: () => translations.t('Form.validations.default'),
      required: (field, _) => {
        if (!field.name) {
          return translations.t('Form.validations.required');
        }

        const customTranslationKey = `validation.custom.${field.name.toLowerCase()}.required`;

        return translations.te(customTranslationKey) || translations.te(customTranslationKey, fallbackLocale)
          ? translations.t(customTranslationKey)
          : translations.t('Form.validations.required');
      },
      email: () => translations.t('Form.validations.email'),
      min: (_, min) => translations.t('Validation.min.string', { min }),
      min_value: (_, minValue) => translations.t('Validation.minValue.string', { minValue }),
      max: (_, max) => translations.t('Validation.max.string', { max }),
      max_value: (_, maxValue) => translations.t('Validation.maxValue.string', { maxValue }),
    },
  };

  return dictionary;
};

export const getCSRFToken = () => document.querySelector('meta[name="csrf-token"]').getAttribute('content');

export const resetForm = (data) => {
  Object.keys(data).forEach((name) => {
    set(data, [name, 'value'], null);
  });
};

export const setInfoInForm = (data, customerInfo) => {
  if (customerInfo) {
    Object.keys(data).forEach((name) => {
      if (
        customerInfo[name] !== undefined &&
        (data[name].value === null || data[name].value === undefined || data[name].value.length === 0)
      ) {
        set(data, [name, 'value'], customerInfo[name]);
      }
    });
  }
};

export const setCustomerCountryInForm = (data, customerInfo) => {
  if (customerInfo && customerInfo.country && data.country) {
    set(data, ['country', 'value'], customerInfo.country);
  }
};

export const isEmail = (emailOrPhone) => emailOrPhone.includes('@');

export const getComponent = (type) => getFieldComponentForType(type);

export const getAddressLines = (address) =>
  address.addressLines ? address.addressLines.map((address) => htmlEntities(address)).join('<br />') : '';

export const getPasswordRequirements = () => [
  'minLength',
  'specialChars',
  'englishUppercaseCharacters',
  'base10Digit',
  'noConsecutiveCharacters',
];

export const htmlEntities = (str) => String(str).replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const populateFieldValueInFieldset = (fields, field, value) => {
  if (!fields[field]) {
    return;
  }

  if (!!fields[field].options && fields[field].options.length > 0) {
    const cleanSelectStr = (str) => str.trim().replace('-', '').toLowerCase();
    // See Misc section of: https://agile.richemont.com/confluence/pages/viewpage.action?pageId=205573949
    const valueToSet =
      fields[field].options.find(
        (ele) =>
          cleanSelectStr(ele.value) === cleanSelectStr(value) || cleanSelectStr(ele.label) === cleanSelectStr(value),
      )?.value ?? fields[field].options[0].value;
    set(fields, [field, 'value'], valueToSet);
    return;
  }

  set(fields, [field, 'value'], value);
};

export const configureVeeValidate = () => {
  defineRule('required', required);
  defineRule('email', email);
  defineRule('regex', regex);
  defineRule('confirmed', confirmed);
  defineRule('min', min);
  defineRule('min_value', min_value);
  defineRule('max', max);
  defineRule('max_value', max_value);

  configure({
    generateMessage: localize(getTextDictionaryForForm()),
  });

  setLocale(window.Phoenix.i18n.locale);
};

export const formatSelectOptions = (options) =>
  options.map((option) => ({ label: window.Phoenix.i18n.t(`Form.options.${option}`), value: option }));
